import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "cy:inline-flex cy:items-center cy:justify-center cy:gap-2 cy:whitespace-nowrap cy:rounded-md cy:text-sm cy:font-medium cy:transition-all cy:cursor-pointer cy:disabled:pointer-events-none cy:disabled:opacity-50 cy:[&_svg]:pointer-events-none cy:[&_svg:not([class*=size-])]:size-4 cy:shrink-0 cy:[&_svg]:shrink-0 cy:outline-none cy:focus-visible:border-ring cy:focus-visible:ring-ring/50 cy:focus-visible:ring-[3px] cy:aria-invalid:ring-destructive/20 cy:dark:aria-invalid:ring-destructive/40 cy:aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "cy:bg-primary cy:text-primary-foreground cy:shadow-xs cy:hover:bg-primary/90",
        destructive:
          "cy:bg-destructive cy:text-white cy:shadow-xs cy:hover:bg-destructive/90 cy:focus-visible:ring-destructive/20 cy:dark:focus-visible:ring-destructive/40 cy:dark:bg-destructive/60",
        outline:
          "cy:border cy:bg-background cy:shadow-xs cy:hover:bg-accent cy:hover:text-accent-foreground cy:dark:bg-input/30 cy:dark:border-input cy:dark:hover:bg-input/50",
        secondary:
          "cy:bg-secondary cy:text-secondary-foreground cy:shadow-xs cy:hover:bg-secondary/80",
        ghost:
          "cy:hover:bg-accent cy:hover:text-accent-foreground cy:dark:hover:bg-accent/50",
        link: "cy:text-primary cy:underline-offset-4 cy:hover:underline",
      },
      size: {
        default: "cy:h-9 cy:px-4 cy:py-2 cy:has-[>svg]:px-3",
        sm: "cy:h-8 cy:rounded-md cy:gap-1.5 cy:px-3 cy:has-[>svg]:px-2.5",
        lg: "cy:h-10 cy:rounded-md cy:px-6 cy:has-[>svg]:px-4",
        icon: "cy:size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
